import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements, PaymentRequestButtonElement } from "@stripe/react-stripe-js";

import { Form, Radio, Typography, Input, Select, Row, Col, Divider } from "antd";
import styles from "./ExperienceBooking.module.css";

const { Text, Title } = Typography;
const { Option } = Select;

const PaymentBlock = ({ form }) => {
    const selectedPayment = Form.useWatch("paymentMethod", form);
    const stripe = useStripe();
    const elements = useElements();
    const [paymentRequest, setPaymentRequest] = useState(null);
    const [canMakePayment, setCanMakePayment] = useState(false);

    useEffect(() => {
        if (stripe && elements) {
            const pr = stripe.paymentRequest({
                country: 'US',
                currency: 'usd',
                total: {
                    label: 'Total',
                    amount: 40000,
                },
                requestPayerName: true,
                requestPayerEmail: true,
                disableWallets:['link']
            });

            pr.canMakePayment().then(result => {
                if (result) {
                    setCanMakePayment(true);
                }
            });

            setPaymentRequest(pr);
        }
    }, [stripe, elements]);

    return (
        <>
            <Title level={5} className="mb-m">How would you like to pay?</Title>
            <Form.Item name="paymentMethod" initialValue="full" style={{ marginBottom: 32 }}>
                <Radio.Group style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    <Radio className={`${styles.radioReverse} ${selectedPayment === "full" ? styles.active : ""}`} value="full">
                        <div>
                            <Text strong>Pay the total amount today</Text>
                            <br />
                            <Text type="secondary">
                                Your card will be charged the total amount of <b>$400.00</b> today.
                            </Text>
                        </div>
                    </Radio>
                    <Radio className={`${styles.radioReverse} ${selectedPayment === "cash" ? styles.active : ""}`} value="cash">
                        <div>
                            <Text strong>Pay deposit today & the balance in cash</Text>
                            <br />
                            <Text type="secondary">
                                Your card will be charged $40 today and $360 will be due in cash on the day of trip.
                            </Text>
                        </div>
                    </Radio>
                    <Radio className={`${styles.radioReverse} ${selectedPayment === "later" ? styles.active : ""}`} value="later">
                        <div>
                            <Text strong>Pay deposit today & the balance later</Text>
                            <br />
                            <Text type="secondary">
                                Your card will be charged $40 today and $360 24h before your trip.
                            </Text>
                        </div>
                    </Radio>
                </Radio.Group>
            </Form.Item>
            <Title level={5} className="mb-m">Payment Information</Title>
            {canMakePayment && paymentRequest ? (
                <div style={{ marginBottom: 24 }}>
                    <PaymentRequestButtonElement
                        options={{ paymentRequest }}
                        style={{ paymentRequestButton: { type: "default", theme: "dark", height: "40px", width: "500px" } }}
                    />
                </div>
            ) : (
                <p>Apple Pay / Google Pay is not available in this browser.</p>
            )}
            <Divider plain className="my-s">or</Divider>
            <Form.Item
                label="Name On Card"
                name="cardName"
                rules={[{ required: true, message: "Please enter the name on card" }]}
            >
                <Input placeholder="Enter full name on card" />
            </Form.Item>
            <Form.Item
                label="Card Number"
                name="cardNumber"
                rules={[{ required: true, message: "Please enter your card number" }]}
            >
                <div className={styles.stripeInputs}>
                    <CardNumberElement options={{ showIcon: true }} />
                </div>
            </Form.Item>
            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item
                        label="Expiration Date"
                        name="expiry"
                        rules={[{ required: true, message: "Enter expiry date" }]}
                    >
                        <div className={styles.stripeInputs}>
                            <CardExpiryElement />
                        </div>
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="CVC"
                        name="cvc"
                        rules={[{ required: true, message: "Enter CVC" }]}
                    >
                        <div className={styles.stripeInputs}>
                            <CardCvcElement className={styles.cardInput} />
                        </div>
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item
                        label="Country"
                        name="country"
                        rules={[{ required: true, message: "Select a country" }]}
                    >
                        <Select placeholder="Choose a country">
                            <Option value="us">United States</Option>
                            <Option value="in">India</Option>
                            <Option value="ca">Canada</Option>
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="ZIP Code"
                        name="zip"
                        rules={[{ required: true, message: "Enter ZIP code" }]}
                    >
                        <Input placeholder="ZIP Code" />
                    </Form.Item>
                </Col>
            </Row>
        </>
    );
};

export default PaymentBlock;

PaymentBlock.propTypes = {
    form: PropTypes.object
};
